import type { UserManagerSettings } from 'oidc-client-ts';
import { WebStorageStateStore } from 'oidc-client-ts';

// Cognito Hosted UI + OAuth2 Authorization Code + PKCE.
//
// Why explicit `metadata` instead of relying on OIDC discovery: Cognito's
// `.well-known/openid-configuration` for a user pool does NOT publish the
// `token_endpoint` / `userinfo_endpoint` / `end_session_endpoint` (they live on
// the Hosted UI domain, not the issuer host). oidc-client-ts needs the token
// endpoint to complete the code exchange, so we seed the full metadata set
// ourselves — issuer + jwks on the pool host, the OAuth endpoints on the
// Hosted UI domain. This also spares every login a discovery round-trip.

const trimTrailingSlash = (value: string): string => value.replace(/\/$/, '');

export interface CognitoEnv {
  region: string;
  userPoolId: string;
  clientId: string;
  /** Full Hosted UI origin, e.g. https://juror-portal.auth.us-east-1.amazoncognito.com */
  domain: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  scope: string;
}

export const readEnv = (): CognitoEnv | null => {
  const env = import.meta.env;
  const region = env.VITE_COGNITO_REGION?.trim();
  const userPoolId = env.VITE_COGNITO_USER_POOL_ID?.trim();
  const clientId = env.VITE_COGNITO_CLIENT_ID?.trim();
  const domain = env.VITE_COGNITO_DOMAIN?.trim();

  // Auth is only wired when the pool is provisioned AND a real API backs it.
  // In mock/dev mode (no API base URL) the app runs without a login.
  if (!region || !userPoolId || !clientId || !domain) {
    return null;
  }

  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173';

  return {
    region,
    userPoolId,
    clientId,
    domain: trimTrailingSlash(domain),
    redirectUri: env.VITE_COGNITO_REDIRECT_URI?.trim() || `${origin}/auth/callback`,
    postLogoutRedirectUri:
      env.VITE_COGNITO_LOGOUT_REDIRECT_URI?.trim() || `${origin}/`,
    scope: env.VITE_COGNITO_SCOPE?.trim() || 'openid email profile',
  };
};

/**
 * Returns oidc-client-ts settings when Cognito is fully configured, otherwise
 * `null` (auth disabled — mock/dev mode). Reading env at call time keeps this
 * testable and avoids evaluating `window` at module load.
 */
export const buildCognitoSettings = (): UserManagerSettings | null => {
  const env = readEnv();
  if (!env) return null;

  const issuer = `https://cognito-idp.${env.region}.amazonaws.com/${env.userPoolId}`;

  return {
    authority: issuer,
    client_id: env.clientId,
    redirect_uri: env.redirectUri,
    response_type: 'code',
    scope: env.scope,
    // Refresh silently via the refresh_token grant (no hidden iframe / silent
    // redirect page needed — Cognito issues a refresh token in the code flow).
    automaticSilentRenew: true,
    // Drop the `code`/`state` query params from the URL after the callback.
    loadUserInfo: false,
    // Persist across reloads so a refresh doesn't bounce the user to login.
    userStore:
      typeof window !== 'undefined'
        ? new WebStorageStateStore({ store: window.localStorage })
        : undefined,
    metadata: {
      issuer,
      jwks_uri: `${issuer}/.well-known/jwks.json`,
      authorization_endpoint: `${env.domain}/oauth2/authorize`,
      token_endpoint: `${env.domain}/oauth2/token`,
      userinfo_endpoint: `${env.domain}/oauth2/userInfo`,
      end_session_endpoint: `${env.domain}/logout`,
    },
  };
};
